import React, { memo, forwardRef, useImperativeHandle, useState, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";
import * as yup from "yup";

import api from "services/api";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors from "common/serve-request-errors";
import AppButton from "components/app-button";
import AppInput from "components/app-input";
import { useFormik } from "formik";
import ERRORS from "common/errors";

export const AppConfirmDeleteModal = (props, ref) => {
	const dispatch = useDispatch();
	const [id, setId] = useState();
	const [visible, setVisible] = useState(false);
	const [isSubmiting, setIsSubmiting] = useState(false);
	const initialValues = useMemo(() => ({ cancelRemark: "" }), []);
	const formik = useFormik({
		initialValues,
		validationSchema: yup.object({
			cancelRemark: yup.string().required(ERRORS.REQUIRED),
		}),
		onSubmit: (values) => {
			onHandleConfirm(values);
		},
	});

	const onHandleShow = useCallback((data) => {
		setId(data);
		setVisible(true);
	}, []);

	const onHandleDismiss = useCallback(() => {
		setVisible(false);
	}, []);

	//prettier-ignore
	const onHandleConfirm = useCallback(async (values) => {
		let response = null;

		setIsSubmiting(true);

		try {
			const payload = {ids: [id], remarks: values.cancelRemark}
			await api.post.claims.cancel(payload);
			response = true;
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			setIsSubmiting(false);
		}

		if (response) {
			onHandleDismiss();
			dispatch(promptAlertMessage({ message: "Claim has been cancelled successfully" }));
			props.onHandleGetList()
		}
	}, [dispatch, onHandleDismiss, id, props]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	return (
		<Modal classes={{ root: "app-claim-confirm-delete-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="confirm-delete-modal" aria-describedby="confirm-delete-modal">
			<div className="confirm-delete-modal">
				<div className="confirm-delete-modal__main">
					<div className="confirm-delete-modal__header">
						<p className="confirm-delete-modal__title">Confirm Claim Cancellation</p>
						<p className="confirm-delete-modal__description">Are you sure you want to cancel the claim application? This action cannot be undone. The canceled claim days will be returned to the employee claim balance.</p>
					</div>

					<form className="confirm-delete-modal__body" onSubmit={formik.handleSubmit}>
						{/* prettier-ignore */}
						<AppInput required multiline multilineRow={4} maxLength={255} type="text" name="cancelRemark" label="Cancel Remark" placeholder="Enter cancel remark" value={formik.values.cancelRemark} error={formik.errors.cancelRemark} touched={formik.touched.cancelRemark} disabled={isSubmiting} onChange={formik.handleChange} />

						<div className="confirm-delete-modal__button-container">
							<AppButton type="button" label="Cancel" outline onClick={onHandleDismiss} disabled={isSubmiting} />
							<AppButton type="submit" label="Confirm" disabled={isSubmiting} />
						</div>
					</form>
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppConfirmDeleteModal));

AppConfirmDeleteModal.propTypes = {
	ref: PropTypes.object,
};
